// ============================ dashboard.slice.ts ============================

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IApiResponse } from "../sagas/dashboardSaga";

export interface DashboardLoadingState {
  vdiTransactions: boolean;
  smartCoolerProducts: boolean;
  alerts: boolean;
  quikPickProducts: boolean;
}

export interface DashboardState {
  dashboard: IApiResponse;
  loading: DashboardLoadingState;
  error: any;
}

export const initialState: DashboardState = {
  dashboard: {
    data: {
      vdiTransactions: {
        mmsDevices: [],
        mmsMarkets: [],
        mmsProducts: [],
        mmsSales: [],
      },
      alerts: [],
      quikPickMasterProducts: {
        total: 0,
        cvReady: 0,
      },
      smartCoolerProducts: {
        total: 0,
      },
      startDate: "",
      endDate: "",
    },
    success: false,
    message: "",
  },

  loading: {
    vdiTransactions: false,
    smartCoolerProducts: false,
    alerts: false,
    quikPickProducts: false,
  },

  error: false,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,

  reducers: {
    clearDashboardData: (state) => {
      state.dashboard = initialState.dashboard;
      state.loading = initialState.loading;
      state.error = false;
    },

    // VDI Transactions
    getVdiTransactionData: (state, action: PayloadAction<any>) => {
      state.loading.vdiTransactions = true;
      state.error = false;
    },

    getVdiTransactionDataSuccess: (
      state,
      action: PayloadAction<IApiResponse>,
    ) => {
      state.loading.vdiTransactions = false;

      state.dashboard = {
        ...state.dashboard,
        data: {
          ...state.dashboard.data,
          vdiTransactions: { ...action?.payload?.data?.vdiTransactions },
        },
      };
    },

    getVdiTransactionDataFailure: (state,action: PayloadAction<any>) => {
      state.loading.vdiTransactions = false;
      state.error = action.payload || true;
    },

    // Quikpick Products
    getQuikpickProductData: (state) => {
      state.loading.quikPickProducts = true;
      state.error = false;
    },
    getQuikpickProductDataSuccess: (state, action: PayloadAction<any>) => {
      state.loading.quikPickProducts = false;
      state.dashboard = {
        ...state.dashboard,
        data: {
          ...state.dashboard.data,
          quikPickMasterProducts: action?.payload?.data?.quikPickMasterProducts,
        },
      };
    },
    getQuikpickProductDataFailure: (state, action: PayloadAction<any>) => {
      state.loading.quikPickProducts = false;
      state.error = action.payload || true;
    },

    // Smart Cooler Products
    getSmartCoolerProductData: (state, action: PayloadAction<any>) => {
      state.loading.smartCoolerProducts = true;
      state.error = false;
    },

    getSmartCoolerProductDataSuccess: (state, action: PayloadAction<any>) => {
      state.loading.smartCoolerProducts = false;
      state.dashboard = {
        ...state.dashboard,
        data: {
          ...state.dashboard.data,
          smartCoolerProducts: action?.payload?.data?.smartCoolerProducts,
        },
      };
    },

    getSmartCoolerProductDataFailure: (state, action: PayloadAction<any>) => {
      state.loading.smartCoolerProducts = false;
      state.error = action.payload || true;
    },

    // Alerts
    getAlertData: (state,action: PayloadAction<any>) => {
      state.loading.alerts = true;
      state.error = false;
    },

    getAlertDataSuccess: (state, action: PayloadAction<any>) => {
      state.loading.alerts = false;
      state.dashboard = {
        ...state.dashboard,
        data: {
          ...state.dashboard.data,
          alerts: action?.payload?.data?.alerts,
        },
      };
    },
    getAlertDataFailure: (state, action: PayloadAction<any>) => {
      state.loading.alerts = false;
      state.error = action.payload || true;
    },
  },
});

const dashboardSliceReducer = dashboardSlice.reducer;

export const {
  clearDashboardData,
  getVdiTransactionData,
  getVdiTransactionDataSuccess,
  getVdiTransactionDataFailure,
  getAlertData,
  getAlertDataSuccess,
  getAlertDataFailure,
  getQuikpickProductData,
  getQuikpickProductDataSuccess,
  getQuikpickProductDataFailure,
  getSmartCoolerProductData,
  getSmartCoolerProductDataSuccess,
  getSmartCoolerProductDataFailure,
} = dashboardSlice.actions;

export default dashboardSliceReducer;
